import { useState, type ReactNode } from "react";

import {
  actorEmail,
  ALL_REPORTS,
  auditStamp,
  AVAILABILITY_AUDIT_PATH,
  AVAILABILITY_PATH,
  eventsOf,
  isReportTool,
  liveWithinSecondsOf,
  PAUSED_MESSAGE_KEY,
  pauseChange,
  pausedListText,
  pausedOf,
  pausedToolsOf,
  settingDefaultText,
  settingsParamsOf,
  toolsOf,
  unknownToolsOf,
  type AuditPage,
  type McpAvailability,
  type McpAvailabilityWrite,
  type McpCatalog,
  type McpSettings,
} from "../../api/mcp";
import { Gate } from "../../components/authority/Gate";
import { settingAnchor } from "../../components/settings/EditableSetting";
import { ResourceView } from "../../components/states";
import { Badge, Card, Notice, SearchField } from "../../components/ui";
import { ConfirmDialog } from "../../components/write/ConfirmDialog";
import { WriteResult } from "../../components/write/EffectTiming";
import { Reason } from "../../components/write/ReasonText";
import { WriteFailure } from "../../components/write/WriteFailure";
import type { AppConfig } from "../../config";
import { Button } from "../../f7";
import { useResource, type Resource } from "../../hooks/useResource";
import { useWrite, type WriteState } from "../../hooks/useWrite";

/**
 * Paused tools — switch tools off for EVERY merchant while a Foodics API
 * behind them is down (docs/products/mcp.md › "Paused tools panel").
 *
 * A section of Policy → Settings, beside AI apps, for the same reason AI apps
 * is one: it is a setting of MCP, and Policy's tabs are what a change applies
 * to, not one tab per knob.
 *
 * THE WRITE REPLACES THE WHOLE LIST. `POST /admin/availability` has no add-one
 * or remove-one: `["search_orders"]` while `["reports"]` is stored resumes
 * every report. So the body is always built from every checkbox, never from
 * the one just clicked.
 *
 * `reports` is ONE ENTRY, not the current report names: it also covers
 * reports MCP adds later. While it is checked, every report box shows checked
 * and disabled (covered); unchecking it reveals, and sends, only the report
 * names checked on their own.
 *
 * Pausing is not a plan change. A paused tool keeps its caps on every tier;
 * nothing here says "gated".
 */
export function PausedToolsPanel({ config }: { config: AppConfig }) {
  const availability = useResource<McpAvailability>(config, AVAILABILITY_PATH);
  const catalog = useResource<McpCatalog>(config, "/mcp/admin/catalog");
  const audit = useResource<AuditPage>(config, `${AVAILABILITY_AUDIT_PATH}?limit=1`);
  const settings = useResource<McpSettings>(config, "/mcp/admin/settings");
  const write = useWrite<McpAvailabilityWrite>(config);

  // null = "as stored". Held apart so a reload of the stored list is what the
  // boxes show until the operator touches one.
  const [allPick, setAllPick] = useState<boolean | null>(null);
  const [namePicks, setNamePicks] = useState<string[] | null>(null);
  const [query, setQuery] = useState("");
  const [confirming, setConfirming] = useState<"change" | "resumeAll" | null>(null);
  const [result, setResult] = useState<McpAvailabilityWrite | null>(null);

  const a = availability.state.kind === "ready" ? availability.state.data : null;
  const catalogTools = catalog.state.kind === "ready" ? toolsOf(catalog.state.data) : [];
  const stored = pausedOf(a);
  const storedNames = stored.filter((e) => e !== ALL_REPORTS);
  const all = allPick ?? stored.includes(ALL_REPORTS);
  const picked = namePicks ?? storedNames;

  // The catalog, plus any stored name it no longer lists — shown so it can be
  // unchecked, rather than silently re-sent and refused.
  const known = new Set(catalogTools);
  const tools = [...catalogTools, ...storedNames.filter((n) => !known.has(n))];
  const reports = tools.filter(isReportTool);
  const others = tools.filter((t) => !isReportTool(t));

  const next = [...(all ? [ALL_REPORTS] : []), ...tools.filter((t) => picked.includes(t))];
  const dirty = !sameSet(next, stored);
  const sending = confirming === "resumeAll" ? [] : next;
  const change = pauseChange(stored, sending, catalogTools);
  const seconds = liveWithinSecondsOf(a);
  const busy = write.state.kind === "submitting";
  const unknown = refusedNames(write.state);

  function reset() {
    setAllPick(null);
    setNamePicks(null);
  }

  function toggle(name: string, on: boolean) {
    setNamePicks(on ? [...picked, name] : picked.filter((n) => n !== name));
  }

  function submit(reason: string) {
    void write
      .submit(AVAILABILITY_PATH, {
        method: "POST",
        body: JSON.stringify({ paused: sending, reason }),
      })
      .then((r) => {
        if (!r) return;
        setResult(r);
        setConfirming(null);
        reset();
        // Re-read both: a lost reply is indistinguishable from a failed write,
        // and the header line is the audit row this write just made.
        availability.reload();
        audit.reload();
      });
  }

  function reloadCatalog() {
    write.reset();
    setConfirming(null);
    reset();
    catalog.reload();
    availability.reload();
  }

  const q = query.trim().toLowerCase();
  const shown = (names: string[]) => (q ? names.filter((n) => n.toLowerCase().includes(q)) : names);

  return (
    <div id="paused-tools" className="scroll-mt-4">
      <Card
        title="Paused tools"
        aside={<HeaderLine availability={availability.state} audit={audit.state} />}
        note={
          <>
            Hide tools from every merchant while the Foodics service behind them is down. New
            chats stop seeing them {within(seconds)}; chats already open are told the tool is
            temporarily unavailable. No restart, no plan change.
          </>
        }
      >
        <ResourceView state={availability.state} what="the paused tools" onRetry={availability.reload} />
        {availability.state.kind === "ready" && catalog.state.kind !== "ready" && (
          <ResourceView state={catalog.state} what="the tool catalog" onRetry={catalog.reload} />
        )}

        {availability.state.kind === "ready" && catalog.state.kind === "ready" && (
          <div className="flex flex-col gap-4">
            <SearchField
              value={query}
              onChange={setQuery}
              placeholder="search tools…"
              label="Search tools"
            />

            <label className="flex flex-wrap items-baseline gap-x-2 text-sm">
              <input
                type="checkbox"
                checked={all}
                onChange={(e) => setAllPick(e.target.checked)}
              />
              <span className="font-medium">All reports ({reports.length})</span>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                pauses every report, including ones MCP adds later
              </span>
            </label>

            <ToolGroup title="Reports">
              {shown(reports).map((t) => (
                <ToolBox
                  key={t}
                  name={t}
                  checked={all || picked.includes(t)}
                  disabled={all}
                  stale={!known.has(t)}
                  onChange={(on) => toggle(t, on)}
                />
              ))}
            </ToolGroup>

            <ToolGroup title={`Other tools (${others.length})`}>
              {shown(others).map((t) => (
                <ToolBox
                  key={t}
                  name={t}
                  checked={picked.includes(t)}
                  stale={!known.has(t)}
                  onChange={(on) => toggle(t, on)}
                />
              ))}
            </ToolGroup>

            <PausedMessage state={settings.state} onRetry={settings.reload} />

            <Gate need="settings:write">
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  outline
                  disabled={busy || stored.length === 0}
                  onClick={() => {
                    write.reset();
                    setConfirming("resumeAll");
                  }}
                >
                  Resume everything
                </Button>
                <Button
                  disabled={busy || !dirty}
                  onClick={() => {
                    write.reset();
                    setConfirming("change");
                  }}
                >
                  Review changes
                </Button>
              </div>
            </Gate>
          </div>
        )}

        {result && (
          <div className="mt-4">
            <WriteResult title="Paused tools saved" onDismiss={() => setResult(null)}>
              <p>
                {pausedToolsOf(result).length === 0
                  ? "Nothing is paused now."
                  : `Paused now: ${pausedToolsOf(result).join(", ")}.`}{" "}
                This is live {within(liveWithinSecondsOf(result))} on every MCP server.
              </p>
            </WriteResult>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={confirming !== null}
        title={
          confirming === "resumeAll"
            ? "Resume every paused tool"
            : change.pauses
              ? "Pause tools for every merchant"
              : "Resume tools for every merchant"
        }
        confirmLabel={change.pauses ? "Pause for every merchant" : "Resume"}
        destructive={change.pauses}
        requireReason
        // L3 only when something is newly cut off from every merchant, as
        // for the other fleet-wide gates. Resuming is L2.
        typeToConfirm={change.pauses ? "every business" : undefined}
        reasonHint="e.g. DA-1400, Foodics Reports API outage"
        busy={busy}
        onCancel={() => !busy && setConfirming(null)}
        onConfirm={submit}
        error={
          <>
            {unknown.length > 0 ? (
              <Notice tone="amber" title="MCP does not know these tools">
                <ul className="list-disc pl-5">
                  {unknown.map((n) => (
                    <li key={n}>
                      <span className="ref">{n}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-1">
                  The catalog changed since this page loaded. Nothing was written.
                </p>
                <div className="mt-2">
                  <Button outline onClick={reloadCatalog}>
                    Reload the catalog
                  </Button>
                </div>
              </Notice>
            ) : (
              <WriteFailure state={write.state} />
            )}
          </>
        }
        effect={
          change.pauses ? (
            <>
              {capitalise(within(seconds))} every merchant&rsquo;s assistant stops seeing these
              tools; chats already open get the paused message. Resuming takes the same time.
            </>
          ) : (
            <>
              {capitalise(within(seconds))} these tools come back for every merchant. No
              restart.
            </>
          )
        }
      >
        <p>This applies to every merchant on every tier. It changes no plan and no cap.</p>
        <ChangeList title="Pausing" allReports={change.allReports === "added"} names={change.pausing} />
        <ChangeList
          title="Resuming"
          allReports={change.allReports === "removed"}
          names={change.resuming}
        />
        <p className="text-xs">
          The whole list MCP will store:{" "}
          <span className="ref">{sending.length === 0 ? "nothing paused" : pausedListText(sending)}</span>
        </p>
      </ConfirmDialog>
    </div>
  );
}

/**
 * The names a 422 refused, from the WRITE's state. Every other write failure
 * is rendered by `WriteFailure`; only this one needs its own remedy (reload
 * the catalog), so it is the only kind looked at here.
 */
function refusedNames(w: WriteState): string[] {
  return w.kind === "invalid" ? unknownToolsOf(w.error.body) : [];
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  const sa = new Set(a);
  const sb = new Set(b);
  return sa.size === sb.size && [...sa].every((x) => sb.has(x));
}

/** "within about 5 minutes", from the API's own number; never "instantly". */
function within(seconds: number | undefined): string {
  if (seconds === undefined) return "within a few minutes";
  if (seconds < 60) return `within about ${seconds} seconds`;
  const m = Math.round(seconds / 60);
  return `within about ${m} minute${m === 1 ? "" : "s"}`;
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function ToolGroup({ title, children }: { title: string; children: ReactNode[] }) {
  return (
    <fieldset className="flex flex-col gap-1">
      <legend className="mb-1 text-xs font-medium text-slate-500 dark:text-slate-400">{title}</legend>
      {children.length === 0 ? (
        <p className="text-xs text-slate-500 dark:text-slate-400">No tool matches.</p>
      ) : (
        <div className="grid gap-x-6 gap-y-1 sm:grid-cols-2">{children}</div>
      )}
    </fieldset>
  );
}

function ToolBox({
  name,
  checked,
  disabled = false,
  stale,
  onChange,
}: {
  name: string;
  checked: boolean;
  disabled?: boolean;
  stale: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <label className="flex min-w-0 items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="ref truncate">{name}</span>
      {stale && <Badge tone="amber">not in the catalog</Badge>}
    </label>
  );
}

function ChangeList({
  title,
  allReports,
  names,
}: {
  title: string;
  allReports: boolean;
  names: string[];
}) {
  // With `reports` turning on, the report names are covered by that line.
  const listed = allReports && title === "Pausing" ? names.filter((n) => !isReportTool(n)) : names;
  if (!allReports && listed.length === 0) return null;
  return (
    <div>
      <p className="font-medium text-slate-950 dark:text-white">{title}:</p>
      <ul className="mt-1 list-disc pl-5">
        {allReports && (
          <li>
            {title === "Pausing"
              ? "All reports, including reports MCP adds later"
              : "The All reports entry: reports MCP adds later will not be paused"}
          </li>
        )}
        {listed.map((n) => (
          <li key={n}>
            <span className="ref">{n}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * "Nothing paused · last change: none", or the count and the newest audit row:
 * who changed the list, to what, when, and why.
 */
function HeaderLine({
  availability,
  audit,
}: {
  availability: Resource<McpAvailability>;
  audit: Resource<AuditPage>;
}) {
  if (availability.kind !== "ready") return null;
  const n = pausedToolsOf(availability.data).length;
  const count = n === 0 ? "Nothing paused" : `${n} tool${n === 1 ? "" : "s"} paused`;

  let last: ReactNode;
  if (audit.kind === "loading") last = "…";
  else if (audit.kind !== "ready") last = "could not read the audit log";
  else {
    const row = eventsOf(audit.data)[0];
    last = !row ? (
      "none"
    ) : (
      <>
        {actorEmail(row.actor)} set it to {pausedListText(row.after)}, {auditStamp(row.timestamp)}
        {row.reason && (
          <>
            {" "}
            — <Reason text={row.reason} />
          </>
        )}
      </>
    );
  }

  return (
    <p className="max-w-md text-right text-xs text-slate-500 dark:text-slate-400" data-paused-header="">
      {count} · last change: {last}
    </p>
  );
}

/** What a merchant's assistant is told when it calls a paused tool. */
function PausedMessage({
  state,
  onRetry,
}: {
  state: Resource<McpSettings>;
  onRetry: () => void;
}) {
  return (
    <div className="rounded-lg bg-slate-100 p-3 text-sm dark:bg-slate-800/60">
      <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
        What merchants are told when they call a paused tool
      </p>
      <ResourceView state={state} what="the paused message" onRetry={onRetry}>
        {(data) => {
          const p = settingsParamsOf(data).find((x) => x.key === PAUSED_MESSAGE_KEY);
          if (!p) {
            return (
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                This MCP does not serve an editable paused message yet; it uses its built-in text.
              </p>
            );
          }
          const fallback = settingDefaultText(p);
          return (
            <>
              {p.exists && p.value ? (
                <p className="mt-1 whitespace-pre-wrap break-words font-mono text-xs">{p.value}</p>
              ) : (
                <>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Not set: merchants see this built-in text
                  </p>
                  {fallback !== null && (
                    <p className="mt-1 whitespace-pre-wrap break-words font-mono text-xs text-slate-400 dark:text-slate-500">
                      {fallback}
                    </p>
                  )}
                </>
              )}
              <p className="mt-2 text-xs">
                <a className="underline" href={`#${settingAnchor(PAUSED_MESSAGE_KEY)}`}>
                  Edit the message
                </a>{" "}
                <span className="text-slate-500 dark:text-slate-400">
                  in Settings above (Messages › Tool paused).
                </span>
              </p>
            </>
          );
        }}
      </ResourceView>
    </div>
  );
}
